import React, { forwardRef, useRef, useCallback } from 'react';
import { Button, ButtonProps } from '@fluentui/react-components';
import './SyButton.scss';

// Extend ButtonProps with custom appearance
type SyButtonAppearance = 'primary' | 'outline' | 'secondary' | 'tertiary' | 'quarterly' | 'ghost' | 'transparent' | 'link';

export interface SyButtonProps extends Omit<ButtonProps, 'appearance'> {
  appearance?: SyButtonAppearance;
  iconOnly?: boolean; // Forces 48px × 48px square button for icon-only buttons
  overrideBackColorLight?: string; // Override background color in light mode
  overrideBackColorDark?: string; // Override background color in dark mode
  overrideHoverBackColorLight?: string; // Override hover background color in light mode
  overrideHoverBackColorDark?: string; // Override hover background color in dark mode
  overrideForegroundColorLight?: string; // Override foreground (text/icon) color in light mode
  overrideForegroundColorDark?: string; // Override foreground (text/icon) color in dark mode
}

// Forward ref so parent components (like MenuTrigger) can obtain the underlying button element
// This is necessary for popovers and menus which need a DOM anchor to compute positioning.
const SyButton = forwardRef<HTMLButtonElement, SyButtonProps>(({ appearance, iconOnly, overrideBackColorLight, overrideBackColorDark, overrideHoverBackColorLight, overrideHoverBackColorDark, overrideForegroundColorLight, overrideForegroundColorDark, className, style, onMouseEnter: userMouseEnter, onMouseLeave: userMouseLeave, ...props }, ref) => {
  // Apply icon-only styles if iconOnly prop is true
  // Force explicit width and height so the button is exactly square (38x38)
  // Do not force borderRadius inline; SyButton.scss provides the default radius (8px).
  const iconOnlyStyle: React.CSSProperties = iconOnly
    ? { minWidth: '38px', width: '38px', minHeight: '38px', height: '38px', padding: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', boxSizing: 'border-box', ...(style ? { ...style } : {}) } as React.CSSProperties
    : ({ ...(style ? { ...style } : {}) } as React.CSSProperties);

  // Apply background and foreground color overrides if provided. Cast to unknown then React.CSSProperties to allow CSS custom properties.
  const hasBgOverride = !!(overrideBackColorLight || overrideBackColorDark);
  const hasHoverOverride = !!(overrideHoverBackColorLight || overrideHoverBackColorDark);
  const hasFgOverride = !!(overrideForegroundColorLight || overrideForegroundColorDark);

  const finalStyle: React.CSSProperties = (() => {
    // Always start from caller-provided iconOnlyStyle (which already includes merged style)
    const cssVars: Record<string, string> = {};
    if (hasBgOverride) {
      cssVars['--sy-button-bg-light'] = overrideBackColorLight || 'inherit';
      cssVars['--sy-button-bg-dark'] = overrideBackColorDark || 'inherit';
    }
    if (hasHoverOverride) {
      cssVars['--sy-button-hover-bg-light'] = overrideHoverBackColorLight || 'inherit';
      cssVars['--sy-button-hover-bg-dark'] = overrideHoverBackColorDark || 'inherit';
    }
    if (hasFgOverride) {
      cssVars['--sy-button-fg-light'] = overrideForegroundColorLight || 'inherit';
      cssVars['--sy-button-fg-dark'] = overrideForegroundColorDark || 'inherit';
    }
    // Merge styles and enforce the visual tokens we own inline so callers or external CSS
    // can't visually override them. Inline styles have higher priority than external CSS.
    const base = { ...iconOnlyStyle, ...cssVars } as unknown as React.CSSProperties;
    return ({ ...base, borderRadius: '8px', overflow: 'hidden' } as React.CSSProperties);
  })();

  let finalClassName = className || '';
  if (hasBgOverride) finalClassName = `${finalClassName} sy-button-override-bg`.trim();
  if (hasHoverOverride) finalClassName = `${finalClassName} sy-button-override-hover`.trim();
  if (hasFgOverride) finalClassName = `${finalClassName} sy-button-override-fg`.trim();
  // Add an explicit class for icon-only so SCSS can target it with high specificity
  // Ensure a root class is always present so SCSS rules for sy-button-root apply
  const combinedClassName = `${finalClassName || ''} sy-button-root${iconOnly ? ' sy-button-iconOnly' : ''}`.trim();
  // internal ref so we can set inline styles on hover to guarantee overrides win
  const innerRef = useRef<HTMLElement | null>(null);

  // merge forwarded ref with innerRef
  const setRef = useCallback((el: HTMLElement | null) => {
    innerRef.current = el;
    if (!ref) return;
    if (typeof ref === 'function') (ref as unknown as (instance: HTMLElement | null) => void)(el);
    else (ref as React.MutableRefObject<HTMLElement | null>).current = el;
    // When the underlying DOM element is attached, enforce inline styles with !important
    // to ensure the visual border-radius and clipping cannot be overridden by external CSS
    // or Fluent UI internal layers. Also set immediate children to the same radius.
    try {
      if (el) {
        el.style.setProperty('border-radius', '8px', 'important');
        el.style.setProperty('overflow', 'hidden', 'important');
        // Apply to immediate children so internal spans/divs that carry backgrounds are rounded too
        const children = Array.from(el.querySelectorAll(':scope > *')) as HTMLElement[];
        children.forEach(child => {
          try {
            child.style.setProperty('border-radius', '8px', 'important');
            child.style.setProperty('overflow', 'hidden', 'important');
          } catch (e) {
            // ignore per-child errors
          }
        });
      }
    } catch (err) {
      // ignore
    }
  }, [ref]);

  // mouse handlers to set/remove inline background-color with priority 'important'
  const handleMouseEnter = (e: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
    if (userMouseEnter) (userMouseEnter as unknown as (ev: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => void)(e);
    if (hasHoverOverride && innerRef.current) {
      try {
        const bg = overrideHoverBackColorLight || 'inherit';
        innerRef.current.style.setProperty('background-color', bg, 'important');
      } catch (err) {
        // ignore
      }
    }
  };

  const handleMouseLeave = (e: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
    if (userMouseLeave) (userMouseLeave as unknown as (ev: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => void)(e);
    if (hasHoverOverride && innerRef.current) {
      try {
        innerRef.current.style.removeProperty('background-color');
      } catch (err) {
        // ignore
      }
    }
  };
  
  switch (appearance) {
    // Primary buttons use the FluentUI primary appearance with custom focus ring
    case 'primary':
      return (
        <Button
          {...(props as ButtonProps)}
          appearance="primary"
          ref={setRef}
          className={`sy-button-primary ${combinedClassName || ''}`}
          style={finalStyle}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        />
      );
    case 'outline':
      // If appearance is 'outline', use 'outline' for FluentUI and add custom class
      // outline buttons have white background with orange border and text
      return (
        <Button
          {...(props as ButtonProps)}
          appearance="outline"
          ref={setRef}
          className={`sy-button-outline-plus ${combinedClassName || ''}`}
          style={finalStyle}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        />
      );
    case 'secondary':
      // If appearance is 'secondary', use 'secondary' for FluentUI and add custom class
      // Secondary buttons have gray background with dark text
      return (
        <Button
          {...(props as ButtonProps)}
          appearance="secondary"
          ref={setRef}
          className={`sy-button-secondary ${combinedClassName || ''}`}
          style={finalStyle}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        />
      );
    case 'tertiary':
      // Tertiary buttons have border, transparent background with dark text
      return (
        <Button
          {...(props as ButtonProps)}
          appearance="secondary"
          ref={setRef}
          className={`sy-button-tertiary ${combinedClassName || ''}`}
          style={finalStyle}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        />
      );
    case 'quarterly':
      // Quarterly buttons have border, transparent background with dark text (same as tertiary), but the border is a bit darker
      return (
        <Button
          {...(props as ButtonProps)}
          appearance="secondary"
          ref={setRef}
          className={`sy-button-quarterly ${combinedClassName || ''}`}
          style={finalStyle}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        />
      );
    case 'ghost':
      // Ghost buttons have no border, transparent background with dark text.
      // But they get a background when on hover
      return (
        <Button
          {...(props as ButtonProps)}
          appearance="transparent"
          ref={setRef}
          className={`sy-button-ghost ${combinedClassName || ''}`}
          style={finalStyle}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        />
      );
    case 'transparent':
      // Transparent buttons have no border, transparent background with dark text.
      // They are similar to ghost, but they don't have a background when on hover
      return (
        <Button
          {...(props as ButtonProps)}
          appearance="transparent"
          ref={setRef}
          className={`sy-button-transparent ${combinedClassName || ''}`}
          style={finalStyle}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        />
      );
    case 'link':
      // Link buttons have no border, transparent background with dark text
      return (
        <Button
          {...(props as ButtonProps)}
          appearance="transparent"
          ref={setRef}
          className={`sy-button-link ${combinedClassName || ''}`}
          style={finalStyle}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        />
      );
    default:
  return <Button {...(props as ButtonProps)} appearance={appearance as ButtonProps['appearance']} ref={setRef} className={combinedClassName} style={finalStyle} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave} />;
  }
});

SyButton.displayName = 'SyButton';

export default SyButton;
